import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Logo } from "../components/app/Logo";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleAuth() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data.user);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex flex-1 relative overflow-hidden bg-zinc-950 grain border-r border-zinc-800/60">
        <div className="relative z-10 p-12 flex flex-col justify-between">
          <Logo />
          <div className="max-w-md">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500/80 mb-4">Welcome back</p>
            <h2 className="text-3xl font-semibold tracking-tight mb-3" style={{ fontFamily: "var(--font-heading)" }}>
              Today's revenue actions are waiting.
            </h2>
            <p className="text-zinc-400 text-sm">
              Sign in to see prioritized revenue moves generated from your order history.
            </p>
          </div>
          <p className="text-xs text-zinc-600">© 2026 Milkath</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12 bg-zinc-950">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-10"><Logo /></div>
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-50 mb-2" style={{ fontFamily: "var(--font-heading)" }}>
            Sign in
          </h1>
          <p className="text-sm text-zinc-400 mb-8">Continue with your Milkath workspace.</p>

          <button
            onClick={startGoogleAuth}
            type="button"
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-white text-zinc-900 hover:bg-zinc-100 font-medium text-sm transition-colors"
            data-testid="login-google-btn"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4">
              <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.4-1.7 4.1-5.5 4.1-3.3 0-6-2.8-6-6.2s2.7-6.2 6-6.2c1.9 0 3.2.8 3.9 1.5l2.7-2.6C16.9 3.1 14.7 2 12 2 6.9 2 2.7 6.1 2.7 12S6.9 22 12 22c6.9 0 11.4-4.8 11.4-11.6 0-.8-.1-1.4-.2-2L12 10.2z" />
            </svg>
            Continue with Google
          </button>

          <div className="my-6 flex items-center gap-3 text-xs text-zinc-600 uppercase tracking-widest">
            <div className="flex-1 h-px bg-zinc-800" />
            <span>or</span>
            <div className="flex-1 h-px bg-zinc-800" />
          </div>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-zinc-400 mb-1.5">Email</label>
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-orange-600/50 focus:border-orange-600 transition-all"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-medium text-zinc-400">Password</label>
                <Link to="/forgot-password" className="text-xs text-orange-500 hover:text-orange-400" data-testid="login-forgot-link">Forgot?</Link>
              </div>
              <input
                type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2.5 text-sm text-zinc-100 focus:outline-none focus:ring-2 focus:ring-orange-600/50 focus:border-orange-600 transition-all"
                data-testid="login-password-input"
              />
            </div>
            {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
            <button
              type="submit" disabled={submitting}
              className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-medium px-4 py-2.5 rounded-lg text-sm transition-colors"
              data-testid="login-submit-btn"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="mt-6 text-sm text-zinc-400 text-center">
            No account?{" "}
            <Link to="/signup" className="text-orange-500 hover:text-orange-400" data-testid="login-signup-link">Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
